import { useLocation } from "react-router";
import { Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/helpers";

export type LegacyPatientInfo = {
  patientId: string;
  name: string;
  dob: string;
  gender: string;
  phone: string;
  address: string;
  programs: string[];
};

const defaultPatient: LegacyPatientInfo = {
  patientId: "PAT_H0054",
  name: "Ash May",
  dob: "01-02-1917",
  gender: "Female",
  phone: "(555) 218-0041",
  address: "123 Wooden St, NY",
  programs: ["CCM", "PCM", "RPM"],
};

function getPatientName(patient: any) {
  if (patient?.name) return patient.name;
  if (patient?.firstName && String(patient.firstName).includes(" ")) return patient.firstName;

  return [patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ");
}

function getPatientAddress(patient: any) {
  return [
    patient?.address,
    patient?.street,
    patient?.address2,
    patient?.city,
    patient?.state,
    patient?.zipCode,
  ]
    .filter(Boolean)
    .join(", ");
}

function getPatientPrograms(patient: any) {
  const programs = patient?.programs;
  if (Array.isArray(programs)) return programs.map((program) => String(program).toUpperCase());
  if (typeof programs === "string") {
    return programs
      .split(",")
      .map((program) => program.trim().toUpperCase())
      .filter(Boolean);
  }
  return [];
}

function mapPatientInfo(patient: any): LegacyPatientInfo | null {
  if (!patient) return null;

  return {
    patientId: patient?.sourcePatientId || patient?.patientId || patient?._id || "",
    name: getPatientName(patient) || "",
    dob: patient?.dateOfBirth || patient?.dob || "",
    gender: patient?.gender || "",
    phone: patient?.primaryNumber || patient?.phone || patient?.mobileNumber || "",
    address: getPatientAddress(patient),
    programs: getPatientPrograms(patient),
  };
}

export function PatientInfoCard({ patient }: { patient?: LegacyPatientInfo }) {
  const location = useLocation();
  const routePatient = mapPatientInfo((location.state as { legacyPatient?: unknown } | null)?.legacyPatient);
  const patientInfo = patient || routePatient || defaultPatient;
  const fields = [
    { label: "Patient ID", value: patientInfo.patientId },
    { label: "Name", value: patientInfo.name },
    { label: "DOB", value: formatDate(patientInfo.dob) },
    { label: "Gender", value: patientInfo.gender },
    { label: "Phone", value: patientInfo.phone },
    { label: "Address", value: patientInfo.address },
  ];

  return (
    <Card className="border border-[#E5E7EB] shadow-sm">
      <CardContent className="p-4 py-0">
        <div className="mb-4 flex">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 text-xs font-medium text-yellow-800">
            <Info className="h-3.5 w-3.5" />
            Data from AnthroCare
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {fields.map((field) => (
            <div key={field.label} className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {field.label}
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-[#111827]">
                {field.value || "-"}
              </p>
            </div>
          ))}
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Care Program(s)
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {patientInfo.programs.length > 0 ? patientInfo.programs.map((program) => (
                <Badge key={program} variant="outline" className="rounded-full">
                  {program}
                </Badge>
              )) : "-"}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
